<script setup>
import { Editor } from '@tiptap/vue-3';
import Icon from '../Icon.vue';
import { onBeforeMount, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import hotkeys from 'hotkeys-js';
import { agentStream } from './aiClient';
import { feedMarkdown, finalMarkdown, markdownAutoHeal } from './markdownAutoHeal';
import { marked } from 'marked';


const {editor} = defineProps({
  editor: Editor
})
const emit = defineEmits(['close']);


const popupRef = ref(null)
const inputRef = ref(null)

function clickAwayListener(e) {
  if (!e.target.closest('.ai-popup')) {
    emit('close')
  }
}

function escListener(e) {
  if (event.key === 'Escape') {
    emit('close')
  }
}

const messages = ref([])
const state = ref('ready')

const { from, to } = editor.state.selection


let context = editor.state.doc.textBetween(Math.max(0, from - 1500), from).trim()
let selection = editor.state.doc.textBetween(from, to).trim()

let agent;
if (context && selection) {
  agent = 'inplace-rewrite'
} else if (context) {
  agent = 'inplace-generate'
} else {
  agent = 'inplace-create'
}

const replaceMode = !!selection
let abortController;
let content = '';

function send() {
    if (event.shiftKey || state.value !== 'ready') {
      return;
    }

    abortController = new AbortController()    
    content = '';
    let prompt = '';
    const userInput = inputRef.value.innerText;
    if (messages.value.length === 0) {
      if (context) {
        prompt += 'I am writing following docuemnt: \n'+
                '\n' + context + '\n\n'
      }
      if (selection) {
        prompt += 'I need to rewrite: \n'+
                '\n' + context + '\n\n'
      }
      prompt += userInput;
    }
    inputRef.value.innerHTML = ''
    console.log('sending prompt', prompt)
    agentStream({
      prompt, 
      memory: messages.value,       
      agent: 'simulator',
      signal: abortController.signal,
      onEvent: event => {

        if (event.type !== 'token') {
          return
        }
  
        const token = event.token
        content += token

        // editor.chain().insertContent(          
        //   token
        // ).run()
        feedMarkdown(token)
      },
      
      onDone: () => {
        messages.value.push({
          role: 'assistant',
          content
        })
        console.log(content)
        finalMarkdown(content)
      }
    })
    messages.value.push({
      role: 'user',
      content: userInput
    })

    // markdownAutoHeal(editor, from)
}

onMounted(() => {

    const { state } = editor;
    const { selection } = state;
    const cursorPos = selection.from; // Get the cursor position  
    const coords = editor.view.coordsAtPos(cursorPos);

    const popup = popupRef.value;

    // Calculate initial popup position
    let popupLeft = coords.left;

    // Get window dimensions
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;

    // Get popup dimensions
    const popupRect = popup.getBoundingClientRect();
    const popupWidth = popupRect.width;
    const popupHeight = popupRect.height;

    let popupTop = coords.top - popupHeight - 15;


    // Ensure the popup is not clipped by the right edge of the window
    if (popupLeft + popupWidth > windowWidth) {
      popupLeft = windowWidth - popupWidth - 10; // Shift left with a 10px margin
    }

    // Ensure the popup is not clipped by the bottom edge of the window
    if (popupTop + popupHeight > windowHeight) {
      popupTop = coords.top - popupHeight - 10; // Shift above the cursor with a 10px margin
    }

    // Ensure the popup is not clipped by the left edge of the window
    if (popupLeft < 0) {
      popupLeft = 10; // Shift right with a 10px margin
    }

    // Position the popup with adjusted values
    popup.style.left = `${popupLeft}px`;
    popup.style.top = `${popupTop}px`;

    const transaction = editor.state.tr.setMeta('charizard.LocationMarker', { add: true, pos: cursorPos });
    editor.view.dispatch(transaction);

    let isDragging = false;
    let offsetX = 0;
    let offsetY = 0;

    popup.addEventListener("mousedown", (e) => {
      isDragging = true;
      offsetX = e.clientX - popup.offsetLeft;
      offsetY = e.clientY - popup.offsetTop;
    });

    popup.addEventListener("mousemove", (e) => {
      if (isDragging) {
        e.stopPropagation();        
        popup.style.left = e.clientX - offsetX + "px";
        popup.style.top = e.clientY - offsetY + "px";
      }
    });

    popup.addEventListener("mouseup", () => {
      isDragging = false;
    });

    setTimeout(() => {
      document.addEventListener('click', clickAwayListener)

    }, 500)
    document.addEventListener('keydown', escListener )
    inputRef.value.focus();


    editor.chain().insertContent({
      type: 'streamingNode',
      attrs: {      
      },
    }).run();


});

function deleteStreamingNodes() {
  document.querySelectorAll('.streaming-node').forEach(n => n.remove())
  // let chain = editor.chain()
  // const nodes = []
  // editor.state.doc.descendants((node, pos) => {
  //   if (node.type.name === 'streamingNode') {
  //     nodes.push([node, pos])      
  //   }
  // });

  // nodes.reverse();
  // nodes.forEach(([node, pos]) => chain = chain.deleteRange({ from: pos, to: Math.min(editor.state.doc.content.size-1, pos + node.nodeSize)}) )

  // chain.run()
}

function apply() {

  editor.chain().insertContent(marked(content)).run()

}

onBeforeUnmount(() => {
  if (abortController) {
    abortController.abort()
  }
  const transaction = editor.state.tr.setMeta('charizard.LocationMarker', { remove: true });
  editor.view.dispatch(transaction);
  document.removeEventListener('click', clickAwayListener )
  document.removeEventListener('keydown', escListener )
  apply()
  deleteStreamingNodes();
})



</script>

<template>
  

  <div     
    class="ai-popup fixed bg-base-200 p-4 z-1 rounded shadow-md w-120 p-2 border-1 border-base-300" 
    ref="popupRef"
   >
    <ul class='messages-list'>
      <li v-for="m in messages.filter(m => m.role === 'user')">{{ m.content }}</li>
    </ul>
    <div class='prompt bg-base-100 border-1 border-base-300'>
      <div class='editable' contenteditable='true' ref="inputRef" @keyup.enter="send">
        
      </div>
      <button 
        @click="send"
        class="btn btn-ghost btn-sm btn-circle round self-end">

        <Icon name='fa6-solid--arrow-up'  :size="16" />
      </button>
      <!-- <textarea  @input="autogrow" /> -->
    </div>  
    <div class='flex flex-wrap py-2 justify-center' v-if="agent === 'inplace-rewrite'">

      <div><button id="action1" class="btn btn-ghost btn-xs">Make shorter</button></div>
      <div><button id="action1" class="btn btn-ghost btn-xs">Make longer</button></div>
      <div><button id="action1" class="btn btn-ghost btn-xs">Make like 5 yo kid</button></div>
      <div><button id="action1" class="btn btn-ghost btn-xs">Fix errors</button></div>
    </div>
  </div>


</template>

<style lang='less' scoped>

  .prompt {

    display: flex;
    align-items: center;

    // width: 300px;
    // border: 1px solid var(--color-primary	) ;
    border-radius: 20px;
    padding: 5px;
    padding-left: 12px;
    
    .editable {      
      padding-top: 2px;
      // min-height: 1rem;
      flex: 1;
      outline: none;
      overflow: auto;
    }
  }

  .messages-list {
    padding-left: 15px;
    padding-bottom: 5px;
    font-size: 11px;
    color: var(--color-info-content	);
    gap: 6px;
    display: flex;
    flex-direction: column;

  }

</style>